/**
 * Settings API service for user preferences and configuration.
 */
import apiClient from './client';
import { UserResponse } from './auth';

export interface SettingsUpdateRequest {
  // Profile
  full_name?: string;
  preferred_name?: string;
  ministry_role?: string;
  denomination?: string;

  // Theological
  theological_tradition?: string;
  bible_translation?: string;

  // UI Preferences
  color_mode?: 'dark' | 'light' | 'auto';
  chat_font?: string;
  sidebar_collapsed?: boolean;
  show_tooltips?: boolean;

  // Notifications
  email_notifications?: boolean;
  weekly_digest?: boolean;
  sms_opt_in?: boolean;

  // API Keys toggle
  use_own_api_keys?: boolean;
}

export interface ApiKeyRequest {
  provider: 'anthropic' | 'openai' | 'straico';
  api_key: string;
}

export interface ApiKeyResponse {
  provider: string;
  masked_key: string;
  key_exists: boolean;
}

export interface ApiKeysStatus {
  use_own_api_keys: boolean;
  keys: {
    anthropic?: string | null;
    openai?: string | null;
    straico?: string | null;
  };
}

export const settingsApi = {
  /**
   * Update user settings (partial updates allowed)
   */
  async updateSettings(settings: SettingsUpdateRequest): Promise<UserResponse> {
    const response = await apiClient.patch('/api/users/me/settings', settings);
    return response.data;
  },

  /**
   * Save or update an encrypted API key for a provider
   */
  async saveApiKey(data: ApiKeyRequest): Promise<ApiKeyResponse> {
    const response = await apiClient.post('/api/users/me/api-keys', data);
    return response.data;
  },

  /**
   * Delete an API key for a specific provider
   */
  async deleteApiKey(provider: 'anthropic' | 'openai' | 'straico'): Promise<void> {
    await apiClient.delete(`/api/users/me/api-keys/${provider}`);
  },

  /**
   * Get status of configured API keys (returns masked versions)
   */
  async getApiKeysStatus(): Promise<ApiKeysStatus> {
    const response = await apiClient.get('/api/users/me/api-keys');
    return response.data;
  },
};
